<script lang="ts">
    import type { Petition } from '../../api/petitions.ts';
    import * as t from '../../translations/Petitions.ts';
    import CaretRightIcon from '../../icons/CaretRightIcon.svelte';
    import UsersIcon from '../../icons/UsersIcon.svelte';
    import StarIcon from '../../icons/StarIcon.svelte';
    import AlarmClockIcon from '../../icons/AlarmClockIcon.svelte';
    import CheckIcon from '../../icons/CheckIcon.svelte';

    export let petitions: Petition[] = [];
    export let signedPetitionIds: Set<number> = new Set();
    export let onSign: (petition: Petition) => void = () => {};

    function safeTranslation(key: string, fallback: string): string {
        const candidate = (t as any)[key];
        if (typeof candidate === 'function') return candidate();
        if (typeof candidate === 'string' && candidate.trim().length > 0) return candidate;
        return fallback;
    }

    function safeSignaturesLabel(count: number): string {
        const candidate = (t as any).Signatures_Label;
        if (typeof candidate === 'function') return candidate(count);
        return `${count} signatures`;
    }

    function isEndingSoon(dateString?: string): boolean {
        if (!dateString) return false;
        const endDate = new Date(dateString);
        const now = new Date();
        const daysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        return daysLeft > 0 && daysLeft <= 7;
    }

    function getDaysLeft(dateString?: string): number {
        if (!dateString) return 0;
        const endDate = new Date(dateString);
        const now = new Date();
        return Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    }

    function getDescription(petition: Petition, maxLength: number = 250): string {
        // Prefer the pre-stripped plain-text alert; `description` now holds the raw
        // CKEditor HTML used by the sign page, which would render as markup here.
        const text = petition.petitionAlert?.shortenedAlert || petition.petitionAlert?.alert || '';
        if (text.length <= maxLength) return text;
        const truncated = text.substring(0, maxLength).replace(/\s+\S*$/, '');
        return truncated + '...';
    }

    function handleCardKeydown(event: KeyboardEvent, petition: Petition): void {
        if (event.currentTarget !== event.target) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSign(petition);
        }
    }

    $: spotCount = petitions.length;
    $: gridCols = spotCount === 1 ? 'md:grid-cols-2' : spotCount === 2 ? 'md:grid-cols-2' : spotCount === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4';
</script>

{#if petitions.length > 0}
    <section class="w-full" aria-label="Featured Petitions">
        <!-- Mobile: taller cards (200px), Tablet: 140px, Desktop: 160px -->
        <div class="grid grid-cols-1 sm:grid-cols-2 {gridCols} gap-4 sm:gap-3 auto-rows-[200px] sm:auto-rows-[140px] md:auto-rows-[160px]">
            {#each petitions as spotlight, index}
                {@const isLargeCardDesktop = spotCount === 1 || spotCount === 2 || index === 0}
                {@const cardSpan = spotCount === 2 ? 'sm:col-span-1 sm:row-span-2' : (isLargeCardDesktop ? 'sm:col-span-2 sm:row-span-2' : '')}
                {@const signed = signedPetitionIds.has(spotlight.id)}
                <div 
                    class="spotlight-card-shell group cursor-pointer rounded-xl overflow-hidden shadow-lg relative isolate transition-[box-shadow] duration-300 ease-in-out hover:shadow-xl focus:ring-2 focus:ring-white focus:ring-offset-2 focus:outline-none {cardSpan}"
                    on:click={() => onSign(spotlight)}
                    on:keydown={(e) => handleCardKeydown(e, spotlight)}
                    role="button"
                    tabindex="0"
                    aria-label={signed
                        ? `Featured petition: ${spotlight.title}. You have signed. Open petition.`
                        : `Featured petition: ${spotlight.title}. Click to sign.`}
                >
                    <div class="spotlight-card-inner absolute inset-0 rounded-xl transition-transform duration-300 ease-in-out transform-gpu will-change-transform [backface-visibility:hidden] [transform:translateZ(0)] group-hover:scale-[1.01]">
                    {#if spotlight.imageUrl}
                        <!-- With image -->
                        <img 
                            src={spotlight.imageUrl} 
                            alt={spotlight.title}
                            class="w-full h-full object-cover rounded-xl transform-gpu [backface-visibility:hidden]"
                        />
                        <div class="absolute inset-0 rounded-xl bg-gradient-to-t from-black/90 via-black/40 to-transparent sm:from-black/80 sm:via-black/30"></div>
                    {:else}
                        <!-- No image: gradient background -->
                        <div class="absolute inset-0 rounded-xl bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 transition-all duration-300 group-hover:from-slate-700 group-hover:via-slate-600 group-hover:to-slate-800"></div>
                    {/if}
                    
                    <!-- Content overlay -->
                    <div class="spotlight-card-overlay absolute inset-0 p-4 sm:p-3 md:p-4 flex flex-col justify-end !text-white">
                    <!-- Badges row -->
                    <div class="flex items-center gap-2 mb-2 sm:mb-1 md:mb-2">
                        <span class="spotlight-featured-badge inline-flex items-center gap-1 px-2.5 py-1 sm:px-2 sm:py-0.5 bg-white/20 backdrop-blur-sm rounded-full text-[11px] sm:text-[9px] md:text-[10px] font-medium uppercase tracking-wide">
                            <StarIcon width="10" height="10" class="sm:w-[8px] sm:h-[8px] md:w-[9px] md:h-[9px]" aria-hidden="true" />
                            <span class="spotlight-featured-badge-text">{safeTranslation('Featured_Label', 'FEATURED')}</span>
                        </span>
                        {#if isEndingSoon(spotlight.endDate)}
                            <span class="spotlight-ending-badge inline-flex items-center gap-1 px-2.5 py-1 sm:px-2 sm:py-0.5 bg-amber-500/90 rounded-full text-[11px] sm:text-[9px] md:text-[10px] font-medium">
                                <AlarmClockIcon width="10" height="10" class="sm:w-[8px] sm:h-[8px] md:w-[9px] md:h-[9px]" aria-hidden="true" />
                                <span class="spotlight-ending-badge-text">{getDaysLeft(spotlight.endDate)}d left</span>
                            </span>
                        {/if}
                    </div>
                    
                    <!-- Title - same size on mobile for all cards, varies on desktop -->
                    <h2 class="spotlight-card-title !font-bold !leading-tight !text-white line-clamp-2 !text-xl mb-2 {isLargeCardDesktop ? 'sm:!text-lg md:!text-xl sm:mb-1 md:mb-2' : 'sm:!text-xs md:!text-sm sm:mb-0'}">
                        {spotlight.title}
                    </h2>
                    
                    <!-- MOBILE: Always show full content (description + button) for ALL cards -->
                    <div class="sm:hidden">
                        <p class="spotlight-card-description !text-white/80 !text-sm !leading-5 mb-3 line-clamp-1">{getDescription(spotlight, 100)}</p>
                        <div class="flex flex-col gap-3">
                            <div
                                class="spotlight-sign-button !min-h-[48px] !text-sm !leading-5 transition-colors w-full rounded !px-2 !py-1 border border-solid inline-flex items-center justify-center {signed
                                    ? '!bg-emerald-50 !text-emerald-800 !border-2 !border-emerald-400 group-hover:!bg-emerald-100'
                                    : '!bg-white !text-gray-900 !border-white group-hover:!bg-gray-200 group-active:!bg-gray-300'}"
                                aria-hidden="true"
                            >
                                <div class="flex gap-2 justify-center items-center">
                                    {#if signed}
                                        <CheckIcon width="18" height="18" class="flex-shrink-0 text-emerald-600" aria-hidden="true" />
                                        <div class="font-medium">{safeTranslation('Signed_State_Label', 'Signed')}</div>
                                    {:else}
                                        <div>{safeTranslation('Sign_Petition_Button', 'Sign Petition')}</div>
                                    {/if}
                                    <div class="my-auto {signed ? 'text-emerald-600' : 'text-[var(--vv-action-center-button-color,#0d9488)]'}">
                                        <CaretRightIcon aria-hidden="true" />
                                    </div>
                                </div>
                            </div>
                            {#if spotlight.signatureCount}
                                <span class="spotlight-signature-count text-white/70 text-xs inline-flex items-center justify-center gap-1.5">
                                    <span class="spotlight-signature-icon flex items-center justify-center flex-shrink-0">
                                        <UsersIcon width="14" height="14" class="opacity-70" aria-hidden="true" />
                                    </span>
                                    <span class="spotlight-signature-text">{safeSignaturesLabel(spotlight.signatureCount)}</span>
                                </span>
                            {/if}
                        </div>
                    </div>
                    
                    <!-- TABLET/DESKTOP: Large cards get full content, small cards get compact -->
                    <div class="hidden sm:block">
                        {#if isLargeCardDesktop}
                            <p class="spotlight-card-description !text-white/80 !text-xs md:!text-sm !leading-5 mb-2 md:mb-3 line-clamp-2">{getDescription(spotlight, 120)}</p>
                            <div class="flex flex-row flex-wrap items-center gap-2 md:gap-3">
                                <div
                                    class="spotlight-sign-button !min-h-[36px] md:!min-h-[40px] !text-xs md:!text-sm !leading-5 transition-colors rounded !px-2 !py-1 border border-solid inline-flex items-center justify-center {signed
                                        ? '!bg-emerald-50 !text-emerald-800 !border-2 !border-emerald-400 group-hover:!bg-emerald-100'
                                        : '!bg-white !text-gray-900 !border-white group-hover:!bg-gray-200 group-active:!bg-gray-300'}"
                                    aria-hidden="true"
                                >
                                    <div class="flex gap-2 items-center">
                                        {#if signed}
                                            <CheckIcon width="16" height="16" class="flex-shrink-0 text-emerald-600 md:w-[18px] md:h-[18px]" aria-hidden="true" />
                                            <div class="font-medium">{safeTranslation('Signed_State_Label', 'Signed')}</div>
                                        {:else}
                                            <div>{safeTranslation('Sign_Petition_Button', 'Sign Petition')}</div>
                                        {/if}
                                        <div class="my-auto {signed ? 'text-emerald-600' : 'text-[var(--vv-action-center-button-color,#0d9488)]'}">
                                            <CaretRightIcon aria-hidden="true" />
                                        </div>
                                    </div>
                                </div>
                                {#if spotlight.signatureCount}
                                    <span class="spotlight-signature-count text-white/70 text-[10px] md:text-xs inline-flex items-center gap-1.5">
                                        <span class="spotlight-signature-icon flex items-center justify-center flex-shrink-0">
                                            <UsersIcon width="14" height="14" class="opacity-70" aria-hidden="true" />
                                        </span>
                                        <span class="spotlight-signature-text">{spotlight.signatureCount.toLocaleString()}</span>
                                    </span>
                                {/if}
                            </div>
                        {:else}
                            <!-- Smaller cards on tablet/desktop: compact with just signature count -->
                            <div class="flex items-center gap-2 mt-0.5">
                                {#if spotlight.signatureCount}
                                    <span class="spotlight-signature-count text-white/70 text-[10px] inline-flex items-center gap-1">
                                        <span class="spotlight-signature-icon flex items-center justify-center flex-shrink-0">
                                            <UsersIcon width="10" height="10" class="opacity-60" aria-hidden="true" />
                                        </span>
                                        <span class="spotlight-signature-text">{spotlight.signatureCount.toLocaleString()}</span>
                                    </span>
                                {/if}
                            </div>
                        {/if}
                    </div>
                    </div>
                </div>
            </div>
        {/each}
        </div>
    </section>
{/if}

<style>
    :global(.spotlight-card-shell) {
        font-size: 1rem !important;
        line-height: 1.5rem !important;
    }

    :global(.spotlight-card-overlay) {
        color: #ffffff !important;
    }

    :global(.spotlight-featured-badge),
    :global(.spotlight-ending-badge) {
        font-size: 0.625rem !important;
        line-height: 0.875rem !important;
        font-weight: 600 !important;
        letter-spacing: 0.08em !important;
    }

    :global(.spotlight-featured-badge-text),
    :global(.spotlight-ending-badge-text) {
        font-size: inherit !important;
        line-height: inherit !important;
        font-weight: inherit !important;
    }

    :global(.spotlight-card-title) {
        color: #ffffff !important;
        font-weight: 700 !important;
    }

    :global(.spotlight-card-description) {
        color: rgba(255, 255, 255, 0.8) !important;
        font-size: 0.875rem !important;
        line-height: 1.25rem !important;
    }

    :global(.spotlight-sign-button) {
        border: 1px solid #ffffff !important;
        background-color: #ffffff !important;
        color: #111827 !important;
        font-size: 0.875rem !important;
        line-height: 1.25rem !important;
        font-weight: 500 !important;
        box-sizing: border-box !important;
    }

    :global(.spotlight-signature-count) {
        color: rgba(255, 255, 255, 0.75) !important;
        font-size: 0.75rem !important;
        line-height: 1rem !important;
        font-weight: 400 !important;
        align-items: center !important;
    }

    :global(.spotlight-signature-icon) {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        flex: 0 0 auto !important;
        align-self: center !important;
        line-height: 1 !important;
    }

    :global(.spotlight-signature-icon :global(svg)) {
        display: block;
        flex: 0 0 auto;
    }

    :global(.spotlight-signature-text) {
        display: inline-block !important;
        margin: 0 !important;
        padding: 0 !important;
        line-height: inherit !important;
        vertical-align: middle !important;
    }
</style>

